import React, { useState, useEffect, Fragment, useMemo, useCallback } from 'react';
import Thermometer from '../components/proposals/Thermometer'; // Path will need update after component rename
import { useResourceOptions, Seniority as SeniorityType } from '../hooks/useResourceOptions';
import { useResourceDetails } from '../hooks/useResourceDetails';
import { useProposals, Proposal } from '../hooks/useProposals'; // Changed from useProjects, Project
//import { useClients } from '../hooks/useClients'; // Added for client filtering
import { Menu, Transition, Dialog } from '@headlessui/react';
import {
    EllipsisVerticalIcon,
    PencilIcon,
    TrashIcon,
    FunnelIcon,
    TableCellsIcon,
    Squares2X2Icon,
    EyeIcon,
    PlusIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { useAuth, ROLE_ADMIN, ROLE_PROJECT_RADAR } from '../contexts/AuthContext';
// Paths for ProjectForm and ProjectList will need update after component renames
import ProposalForm, { DurationUnit } from '../components/proposals/ProposalForm'; // Corrected path
import ProposalList, { ViewMode as ProposalListViewMode } from '../components/proposals/ProposalList'; // Corrected path
import ProposalFilterModal, { ProposalFilterValues } from '../components/proposals/ProposalFilterModal'; // Added import
import { Link } from 'react-router-dom';
import { db } from '../config/firebase'; // Make sure this path is correct
import { collection, getDocs, addDoc, DocumentData, onSnapshot, query, orderBy } from 'firebase/firestore'; // Firebase imports

const MOBILE_BREAKPOINT = 768;

// Placeholder Client interface - ideally this comes from a types file or useClients hook
interface Client {
    id: string;
    name: string;
}

function formatDate(dateString: string | undefined) {
    if (!dateString) return 'N/A'; // Or some other placeholder
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).format(date);
}

// Renamed component from Projects to ProposalsPage
export default function Proposals() {
    const { proposals, loading: loadingProposals, error: proposalsError, addProposal, updateProposal, deleteProposal } = useProposals(); // Removed refetchProposals
    // const { clients, loadingClients, errorClients } = useClients(); // Added for client name lookup in filter - Will be commented out
    const [clients, setClients] = useState<Client[]>([]);
    const [loadingClients, setLoadingClients] = useState(true);
    const [clientsError, setClientsError] = useState<string | null>(null);

    const { types, getTechnologiesByType, seniorities, loading: loadingResourceOptions, error: resourceOptionsError } = useResourceOptions();
    const { getTypeName, getTechnologyName, loading: loadingResourceDetails, error: resourceDetailsError } = useResourceDetails();
    const { user, userHasRole } = useAuth();

    const [editingProposal, setEditingProposal] = useState<Proposal | null>(null); // Renamed
    const [viewMode, setViewMode] = useState<ProposalListViewMode>('table');
    const [showLeadsOnly, setShowLeadsOnly] = useState(false); // This filter might be specific to proposals if they represent leads
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [selectedProposalForDetails, setSelectedProposalForDetails] = useState<Proposal | null>(null); // Renamed

    const [filters, setFilters] = useState<ProposalFilterValues>({ // Typed filters state
        status: [] as string[],
        heatLevel: [] as string[],
        client: '', // This will filter by client name typed by user
        manager: ''
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);

    // Permission to view this page (Proposals)
    const canViewProposals = useMemo(() => { // Renamed
        if (!user) return false;
        // Assuming same roles can view proposals as they could projects
        return userHasRole(ROLE_ADMIN) || userHasRole(ROLE_PROJECT_RADAR);
    }, [user, userHasRole]);

    // Fetch Clients useEffect - NOW WITH REAL-TIME LISTENER
    useEffect(() => {
        if (!canViewProposals) {
            setLoadingClients(false);
            setClients([]);
            return;
        }

        setLoadingClients(true);
        setClientsError(null);

        const clientsQuery = query(collection(db, 'clients'), orderBy('name'));

        const unsubscribeClients = onSnapshot(clientsQuery,
            (snapshot) => {
                const clientsList = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as Client[];
                setClients(clientsList);
                setLoadingClients(false);
                setClientsError(null);
            },
            (error) => {
                console.error("Error listening to clients collection:", error);
                setClientsError("Falha ao carregar clientes em tempo real. " + error.message);
                setLoadingClients(false);
            }
        );

        return () => {
            unsubscribeClients();
        };
    }, [canViewProposals]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (isMobile) {
            setViewMode('cards');
        }
    }, [isMobile]);

    const getSeniorityName = (seniorityId: string): string => {
        const seniority = seniorities.find(s => s.id === seniorityId);
        return seniority ? seniority.name : seniorityId;
    };

    // MODIFIED handleProposalFormSubmit
    const handleProposalFormSubmit = async (
        proposalFormData: Omit<Proposal, 'id' | 'createdAt' | 'updatedAt' | 'clientId'> & { clientId?: string },
        newClientName?: string
    ) => {
        let finalClientId = proposalFormData.clientId;
        let proposalDataForSubmit: Omit<Proposal, 'id' | 'createdAt' | 'updatedAt'>;

        try {
            if (newClientName && newClientName.trim() !== '') {
                // setLoadingClients(true); // No longer needed here, listener handles loading
                const trimmedNewClientName = newClientName.trim();

                // Check if client already exists (important to avoid duplicates from fast typing/submission)
                // This check can use the current `clients` state, which is updated by the listener.
                const existingClient = clients.find(c => c.name.toLowerCase() === trimmedNewClientName.toLowerCase());

                if (existingClient) {
                    finalClientId = existingClient.id;
                } else {
                    const clientsCollection = collection(db, 'clients');
                    const newClientDocData = { name: trimmedNewClientName }; // Firestore will add server timestamp if configured
                    const clientDocRef = await addDoc(clientsCollection, newClientDocData as DocumentData);
                    finalClientId = clientDocRef.id;
                    // DO NOT setClients here directly. Listener will pick it up.
                    // const newClientForState = { id: clientDocRef.id, ...newClientDocData };
                    // setClients(prevClients => [...prevClients, newClientForState].sort((a, b) => a.name.localeCompare(b.name)));
                }
                // setLoadingClients(false); // No longer needed here
            }

            if (!finalClientId) {
                // This should have been caught by the form validation, but as a safeguard:
                throw new Error("Client ID is missing or could not be determined.");
            }

            proposalDataForSubmit = { ...proposalFormData, clientId: finalClientId };

            if (editingProposal && editingProposal.id) {
                await updateProposal(editingProposal.id, proposalDataForSubmit);
            } else {
                await addProposal(proposalDataForSubmit);
            }

            setIsFormVisible(false);
            setEditingProposal(null);

        } catch (error: any) {
            console.error("Error submitting proposal form:", error);
            // The form itself should display specific submission errors based on its own state.
            // This catch is for errors during client creation or if the hooks throw directly.
            // Potentially set a page-level error or re-throw to be caught by a higher boundary.
            // For now, error is logged. The form will show its own submitError for issues it catches.
            // setLoadingClients(false); // Ensure this is also removed if it was part of error path for client creation
            // To show the error in the form, you might need to pass this error back to the form
            // or rely on the form's own error handling if it re-throws correctly.
        }
    };

    // Renamed from handleFormCancel
    const handleProposalFormCancel = () => {
        setIsFormVisible(false);
        setEditingProposal(null);
    };

    // Renamed from handleEdit
    const handleEditProposal = (proposal: Proposal) => {
        setEditingProposal(proposal);
        setIsFormVisible(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Renamed from handleDelete
    const handleDeleteProposal = async (proposalId: string) => {
        if (window.confirm('Tem certeza que deseja excluir esta proposta?')) {
            try {
                await deleteProposal(proposalId);
            } catch (err) {
                console.error('Erro ao excluir proposta:', err);
                // TODO: Show error toast to user
            }
        }
    };

    const handleApplyFilters = (newFilters: ProposalFilterValues) => {
        setFilters(newFilters);
        // Optionally refetch proposals if filtering is done server-side, or rely on client-side filter update
    };

    const handleClearFilters = () => {
        setFilters({
            status: [],
            heatLevel: [],
            client: '',
            manager: ''
        });
    };

    const filteredProposals = proposals.filter(proposal => {
        if (showLeadsOnly && proposal.status !== 'negotiating') return false;
        // Apply new filters
        if (filters.status.length > 0 && !filters.status.includes(proposal.status)) return false;
        if (filters.heatLevel.length > 0 && !filters.heatLevel.includes(proposal.heatLevel)) return false;
        if (filters.client) {
            const client = clients.find((c: Client) => c.id === proposal.clientId);
            if (!client || !client.name.toLowerCase().includes(filters.client.toLowerCase())) {
                return false;
            }
        }
        if (filters.manager && proposal.manager && !proposal.manager.toLowerCase().includes(filters.manager.toLowerCase())) return false;
        return true;
    });

    if (!user) {
        return <div className="text-gray-500">Carregando usuário...</div>;
    }

    if (!canViewProposals) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="text-center">
                    <h1 className="text-2xl font-semibold text-gray-700 mb-4">Acesso Negado</h1>
                    <p className="text-gray-500">Você não tem permissão para visualizar esta página.</p>
                    <Link to="/" className="mt-6 inline-block bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700">
                        Voltar para o Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    if (loadingProposals || loadingResourceOptions || loadingResourceDetails || loadingClients) { // Added loadingClients
        return <div className="text-gray-500">Carregando...</div>;
    }

    const dataFetchError = proposalsError || resourceOptionsError || resourceDetailsError || clientsError;
    if (dataFetchError) {
        return <div className="text-red-500">{dataFetchError}</div>;
    }

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                {/* Text changed from Projetos to Propostas */}
                <h2 className="text-2xl font-bold text-gray-900">Propostas</h2>
                <button
                    onClick={() => {
                        if (isFormVisible && editingProposal) {
                            handleProposalFormCancel();
                        } else {
                            setIsFormVisible(!isFormVisible);
                            if (editingProposal) setEditingProposal(null);
                        }
                    }}
                    className="btn-primary flex items-center"
                >
                    {isFormVisible ? (
                        <>
                            <XMarkIcon className="h-5 w-5 mr-2" />
                            Ocultar Formulário
                        </>
                    ) : (
                        <>
                            <PlusIcon className="h-5 w-5 mr-2" />
                            {/* Text changed from Novo Projeto to Nova Proposta */}
                            Nova Proposta
                        </>
                    )}
                </button>
            </div>

            {isFormVisible && (
                <ProposalForm // Will be renamed from ProjectForm
                    initialProposal={editingProposal ? { // Prop renamed to initialProposal
                        ...editingProposal, // Spread existing proposal
                        // Ensure clientId is used if ProposalForm expects it
                        // client: editingProposal.client, // This was based on old Project interface
                        // clientId: editingProposal.clientId // This should be correct with new Proposal interface
                    } : null}
                    user={user}
                    isEditing={!!editingProposal}
                    onSubmit={handleProposalFormSubmit} // Renamed handler
                    onCancel={handleProposalFormCancel} // Renamed handler
                    // loadingResourceOptions={loadingResourceOptions} // Prop removed as ProposalForm fetches its own options
                    // loadingResourceDetails={loadingResourceDetails} // Prop removed
                    // We need to pass clients for the client dropdown in ProposalForm
                    clients={clients} // Pass the stubbed clients
                    loadingClients={loadingClients} // Pass the stubbed loadingClients
                />
            )}

            {proposals.length > 0 ? (
                <ProposalList // Will be renamed from ProjectList
                    filteredProposals={filteredProposals} // Renamed prop
                    viewMode={viewMode}
                    showLeadsOnly={showLeadsOnly}
                    getTypeName={getTypeName} // Prop UNCOMMENTED
                    getTechnologyName={getTechnologyName} // Prop UNCOMMENTED
                    getSeniorityName={getSeniorityName} // Prop UNCOMMENTED
                    formatDate={formatDate} // Prop UNCOMMENTED
                    handleEdit={handleEditProposal} // Renamed handler
                    handleDelete={handleDeleteProposal} // Renamed handler
                    setSelectedProposalForDetails={setSelectedProposalForDetails} // Renamed prop
                    setShowLeadsOnly={setShowLeadsOnly}
                    setIsFilterModalOpen={setIsFilterModalOpen}
                    setViewMode={setViewMode}
                    isMobile={isMobile}
                    clients={clients} // Pass clients for name display in list
                    loadingClients={loadingClients}
                />
            ) : (
                !loadingProposals && <p className="text-center text-gray-500 py-10">Nenhuma proposta cadastrada ainda.</p> // Text changed
            )}

            {/* Actual Filter Modal */}
            <ProposalFilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                currentFilters={filters}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
            />

            <Dialog
                open={!!selectedProposalForDetails}
                onClose={() => setSelectedProposalForDetails(null)}
                className="relative z-50"
            >
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="mx-auto max-w-sm rounded bg-white p-6">
                        <Dialog.Title className="text-lg font-medium mb-4">
                            Recursos Necessários - {selectedProposalForDetails?.name}
                        </Dialog.Title>
                        <div className="space-y-4">
                            {selectedProposalForDetails?.resources.map((resource, idx) => (
                                <div key={idx} className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded">
                                    {resource.quantity} {getTypeName(resource.typeId)} {getTechnologyName(resource.technologyId)} • {getSeniorityName(resource.seniority)}
                                </div>
                            ))}
                        </div>
                        <div className="mt-6 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setSelectedProposalForDetails(null)}
                                className="btn-primary"
                            >
                                Fechar
                            </button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
        </div>
    );
} 